import React from 'react';
import { Box, Typography, Switch, FormControlLabel, Stack } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { OrganizationPerformanceReportingConfig } from '../../../../_types/performanceReporting';

interface EditProps {
  config: OrganizationPerformanceReportingConfig;
  onChange: (config: OrganizationPerformanceReportingConfig) => void;
}

const Edit: React.FC<EditProps> = ({ config, onChange }) => {
  const handleGlobalDateChange = (newValue: dayjs.Dayjs | null) => {
    const newGlobalDate = newValue && newValue.isValid() ? newValue.toISOString() : null;

    const updatedConfig = {
      ...config,
      globalExpiresAt: newGlobalDate,
    };
    onChange(updatedConfig);
  };

  const handleModuleToggle = (moduleId: number, checked: boolean) => {
    const updatedModules = config.modules.map((module) =>
      module.id === moduleId
        ? {
            ...module,
            status: (checked ? 'ENABLED' : 'DISABLED') as 'ENABLED' | 'DISABLED',
            expiresAt: checked ? module.expiresAt : null,
          }
        : module
    );
    onChange({
      ...config,
      modules: updatedModules,
    });
  };

  const handleModuleDateChange = (moduleId: number, newValue: dayjs.Dayjs | null) => {
    const updatedModules = config.modules.map((module) =>
      module.id === moduleId
        ? {
            ...module,
            expiresAt: newValue && newValue.isValid() ? newValue.toISOString() : null,
          }
        : module
    );
    onChange({
      ...config,
      modules: updatedModules,
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Box>
        <Typography
          variant='subtitleSm'
          sx={{ mb: 1 }}
        >
          {t('manageSolutions.performanceReporting.globalExpiration')}
        </Typography>
        <DatePicker
          sx={{ width: 220, maxWidth: 220 }}
          value={
            config.globalExpiresAt && dayjs(config.globalExpiresAt).isValid() ? dayjs(config.globalExpiresAt) : null
          }
          onChange={handleGlobalDateChange}
          minDate={dayjs().add(1, 'day').startOf('day')}
          slotProps={{
            field: { clearable: true, onClear: () => handleGlobalDateChange(null) },
            textField: {
              size: 'small',
              placeholder: t('manageSolutions.performanceReporting.expirationDate'),
              sx: {
                width: 240,
                maxWidth: 240,
                '& .MuiInputBase-root': {
                  width: 240,
                  maxWidth: 240,
                },
                '&.MuiFormControl-root': {
                  paddingTop: 0,
                  width: 240,
                  maxWidth: 240,
                },
              },
            },
          }}
        />
      </Box>

      <Box>
        <Typography
          variant='subtitleSm'
          sx={{ mb: 3 }}
        >
          {t('manageSolutions.performanceReporting.activeSubscriptions')}
        </Typography>
        <Stack spacing={3}>
          {config.modules.map((module) => {
            const isEnabled = module.status === 'ENABLED';
            return (
              <Box
                key={module.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={isEnabled}
                      onChange={(_, checked) => handleModuleToggle(module.id, checked)}
                      color='success'
                      size='xs'
                    />
                  }
                  label={module.name}
                  sx={{ flex: 1, m: 0 }}
                />
                <DatePicker
                  sx={{ width: 240, maxWidth: 240 }}
                  value={module.expiresAt && dayjs(module.expiresAt).isValid() ? dayjs(module.expiresAt) : null}
                  onChange={(newValue) => handleModuleDateChange(module.id, newValue)}
                  disabled={!isEnabled}
                  minDate={dayjs().add(1, 'day').startOf('day')}
                  slotProps={{
                    field: { clearable: true, onClear: () => handleModuleDateChange(module.id, null) },
                    textField: {
                      size: 'small',
                      placeholder: t('manageSolutions.performanceReporting.expirationDate'),
                      sx: {
                        width: 240,
                        maxWidth: 240,
                        '& .MuiInputBase-root': {
                          width: 240,
                          maxWidth: 240,
                        },
                        '&.MuiFormControl-root': {
                          paddingTop: 0,
                          width: 240,
                          maxWidth: 240,
                        },
                      },
                    },
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
};

export default Edit;
